import { useCallback, useMemo, useState } from 'react';
import { message, Modal, Typography } from 'antd';
import {
  ExclamationCircleOutlined,
  EditOutlined,
  EyeOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  StopOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../../components/common/erp-grid';
import useAuth from '../../../../hooks/useAuth';
import { buildItemColumns } from './itemColumns';
import { itemsApi } from './itemsApi';
// Stage UI-POLISH-LAYOUT-01 follow-up — summary stat-cards + dedicated
// filter strip removed from the page surface. Search + advanced filter
// modal on the grid toolbar replace the inline strip; the grid's own
// header counts (and the page-bottom pagination total) replace the
// per-page summary cards.
//
// Stage 6B.8A-3H follow-up — the quick-view modal was removed; clicking
// a row (or the view action) opens the edit popup directly.
// Stage 6B.8A-3C — simplified create flow. The dropdown opens one of
// 5 lightweight popups instead of jumping straight into the full
// Product Creation Studio. The studio is still reachable via the
// "Advanced Product Studio" option in the dropdown and any Edit
// action from the grid / context menu, so power-users keep their
// existing flow.
import SimpleCreateMenu from './components/SimpleCreateMenu';
import CreateGlassModal     from './components/popups/CreateGlassModal';
import CreateHardwareModal  from './components/popups/CreateHardwareModal';
import CreateProfileModal   from './components/popups/CreateProfileModal';
import CreateJobworkModal   from './components/popups/CreateJobworkModal';
import CreateEcommerceModal from './components/popups/CreateEcommerceModal';
import ItemSetComponentsDrawer from './components/ItemSetComponentsDrawer';
import { SIMPLE_KINDS } from './simpleCreateConstants';
import { loadItemForEdit } from './simpleEditHydrate';
import './ItemMaster.css';

const { Title } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Item Master grid page (Stage 6B.8A-4 + Stage 6B.8A-3C).
//
// Lightweight listing page over the slim core `GET /api/erp/masters/items`
// endpoint. Extension tables (glass / profile / hardware / jobwork /
// ecommerce / images) are NOT loaded by default — when the user wants
// the full record they click Edit → opens Product Creation Studio at
// `/erp/masters/items/studio/:id/edit`.
//
// Stage 6B.8A-3C: the page-level "Create Item" CTA is now a dropdown
// (SimpleCreateMenu) with 5 simplified popups + a link back to the
// Advanced Product Studio. The grid's own add button is intentionally
// disabled (actions.add=false) so there's exactly one create entry
// point on the page surface.
// ─────────────────────────────────────────────────────────────────

const ADAPTER = { list: (query) => itemsApi.list(query) };

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message || 'Failed to load items.';
  message.error({ content: msg, key: 'item-list' });
}

export default function ItemMasterPage() {
  const { me } = useAuth();

  // Permission helper. Master role carries the `*` wildcard; admins /
  // managers / staff / viewer carry explicit `erp.masters.items.*` strings.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);
  const canCreate = hasPerm('erp.masters.items.create');
  const canUpdate = hasPerm('erp.masters.items.update');

  // Reusable query hook owns search / page / sort / advancedFilters.
  // The grid toolbar's built-in search + advanced-filter modal handles
  // every filter that used to live in the external strip.
  const { gridHandlers, refetch, query } = useErpMasterQuery({
    adapter: ADAPTER,
    pageSize: 25,
    onError: defaultOnError
  });

  // Columns are pagination-aware (S.No column closes over current page + size).
  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;
  const columns = useMemo(
    () => buildItemColumns({ currentPage, pageSize }),
    [currentPage, pageSize]
  );

  // ── Simplified popup state (Stage 6B.8A-3C, edit added 3H) ───
  // `openKind` is one of SIMPLE_KINDS.* when a popup is open, else null.
  // `editRecord` + `editInitialValues` are non-null when the popup is
  // mounted in edit mode. Each popup is mounted conditionally with
  // `open={openKind===kind}` so AntD Form state is fresh per open.
  const [openKind, setOpenKind]                   = useState(null);
  const [editRecord, setEditRecord]               = useState(null);
  const [editInitialValues, setEditInitialValues] = useState(null);
  const [viewOnly, setViewOnly]                   = useState(false);

  const handlePickKind   = useCallback((kind) => {
    setEditRecord(null);
    setEditInitialValues(null);
    setViewOnly(false);
    setOpenKind(kind);
  }, []);
  const handleCloseModal = useCallback(() => {
    setOpenKind(null);
    setEditRecord(null);
    setEditInitialValues(null);
    setViewOnly(false);
  }, []);

  // Edit + view share the same load path. `view=true` opens the matching
  // popup in read-only mode (form disabled + Close-only footer); edit
  // opens in update mode (PUT on save).
  const openExistingItem = useCallback(async (recordOrId, { view = false } = {}) => {
    const id = (typeof recordOrId === 'string')
      ? recordOrId
      : (recordOrId && recordOrId.id);
    if (!id) return;
    try {
      const { kind, editRecord: rec, initialValues } = await loadItemForEdit(id);
      setEditRecord(rec);
      setEditInitialValues(initialValues);
      setViewOnly(view);
      setOpenKind(kind);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.message || 'Failed to open item.';
      message.error(msg);
    }
  }, []);
  const goEdit = useCallback((r) => openExistingItem(r, { view: false }), [openExistingItem]);
  const goView = useCallback((r) => openExistingItem(r, { view: true }),  [openExistingItem]);

  // Stage 6B.8A-3H — read-only set components drawer.
  const [setDrawerOpen, setSetDrawerOpen] = useState(false);
  const [setDrawerRecord, setSetDrawerRecord] = useState(null);
  const openSetDrawer = useCallback((record) => {
    if (!record || !record.id) return;
    setSetDrawerRecord(record);
    setSetDrawerOpen(true);
  }, []);
  const closeSetDrawer = useCallback(() => {
    setSetDrawerOpen(false);
    setSetDrawerRecord(null);
  }, []);

  // Refresh + close on successful simple-create save. The success
  // toast is shown by SimpleCreateModalShell; we only refresh + close
  // the modal here.
  const handleSimpleSaved = useCallback(async (_item) => {
    setOpenKind(null);
    try { await refetch(); } catch (_e) { /* surfaced by adapter onError */ }
  }, [refetch]);

  // ── Status changes (activate / deactivate / discontinue) ─────
  const setStatus = useCallback(async (record, status) => {
    const verbs = {
      active:       { gerund: 'Activating',       past: 'activated' },
      inactive:     { gerund: 'Deactivating',     past: 'deactivated' },
      discontinued: { gerund: 'Discontinuing',    past: 'discontinued' }
    };
    const v = verbs[status] || { gerund: 'Updating', past: 'updated' };
    const needsConfirm = status !== 'active';
    if (needsConfirm) {
      const ok = await new Promise((resolve) => {
        let clicked = false;
        Modal.confirm({
          title: `${v.gerund} item?`,
          icon: <ExclamationCircleOutlined />,
          content: `${record.itemCode || ''} · ${record.itemName || ''} will be ${v.past}. Transactions referencing this item are preserved.`,
          okText: v.gerund.replace('ing', '').trim(), // "Deactivate"
          okButtonProps: { danger: status === 'discontinued' },
          cancelText: 'Cancel',
          onOk: () => { clicked = true; },
          onCancel: () => { clicked = false; },
          afterClose: () => resolve(clicked)
        });
      });
      if (!ok) return;
    }
    try {
      await itemsApi.updateStatus(record.id, { status });
      message.success(`Item ${v.past}.`);
      await refetch();
    } catch (err) {
      const msg = err?.response?.data?.error?.message || `Failed to ${v.gerund.toLowerCase()} item.`;
      message.error(msg);
    }
  }, [refetch]);

  // ── Grid props ───────────────────────────────────────────────
  // Stage 6B.8A-3C: actions.add is intentionally false — the page
  // header now carries the dropdown CTA. Stage 6B.8A-3H: view action
  // opens the matching popup in read-only mode.
  const actionsProp = useMemo(() => ({
    add:    false,
    edit:   canUpdate,
    view:   true,
    delete: false   // Item delete is intentionally NOT exposed here; defer per spec.
  }), [canUpdate]);

  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);

  const contextMenuActions = useMemo(() => {
    const items = [
      { key: 'view', label: 'View', icon: <EyeOutlined />, onClick: goView },
      // Stage 6B.8A-3H — show "View Components" only when the row is
      // actually a set. Visible to anyone with view permission.
      {
        key: 'view_components',
        label: 'View Components',
        icon: <AppstoreOutlined />,
        onClick: openSetDrawer,
        visible: (record) => Boolean(record && record.isSetItem)
      }
    ];
    if (canUpdate) {
      items.push({
        key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: goEdit
      });
      items.push({
        key: 'activate', label: 'Activate', icon: <CheckCircleOutlined />,
        onClick: (record) => setStatus(record, 'active'),
        visible: (record) => record && record.status !== 'active'
      });
      items.push({
        key: 'deactivate', label: 'Deactivate', icon: <PoweroffOutlined />,
        onClick: (record) => setStatus(record, 'inactive'),
        visible: (record) => record && record.status === 'active'
      });
      items.push({
        key: 'discontinue', label: 'Discontinue', icon: <StopOutlined />, danger: true,
        onClick: (record) => setStatus(record, 'discontinued'),
        visible: (record) => record && record.status !== 'discontinued'
      });
    }
    return items;
  }, [canUpdate, goView, goEdit, openSetDrawer, setStatus]);

  // `rows` and `total` are pulled directly from the hook return.

  return (
    <ErpMasterShell>
      <div className="erp-item-master">
        <div className="erp-item-master__header">
          <div className="erp-item-master__title-block">
            <Title level={4} style={{ margin: 0 }}>Item Master</Title>
            <p>Manage glass, profile, hardware, jobwork and ecommerce-ready items.</p>
          </div>
          <div className="erp-item-master__header-actions">
            {canCreate ? (
              <SimpleCreateMenu onPick={handlePickKind} />
            ) : null}
          </div>
        </div>

        <ErpDataGrid
          title="Inventory items"
          /* UX redesign — professional grid skin, scoped `.imx-grid`
             overrides live in ItemMaster.css and never touch the shared
             `.erp-grid` baseline. gridId bumped v1 → v2 so the reworked
             column set (SKU folded into Item, Barcode standalone, new
             S.No width) hydrates fresh instead of restoring a stale order. */
          className="imx-grid"
          gridId="erp-masters-items-v2"
          columns={columns}
          rowKey="id"
          rowHeight="compact"
          stickyHeader
          selectable
          actionWidth={64}
          pageSizeOptions={pageSizeOptions}
          searchable
          searchPlaceholder="Search by code, name, SKU, barcode or HSN…"
          searchShortcut
          exportable
          exportFilename="item-master"
          actions={actionsProp}
          emptyTitle="No items found"
          emptyText="Add your first item via the Create Item dropdown to start invoicing, dispatching and stock tracking."
          onEdit={canUpdate ? goEdit : undefined}
          onView={goView}
          onRowClick={canUpdate ? goEdit : goView}
          contextMenuActions={contextMenuActions}
          {...gridHandlers}
        />

        {/* Stage 6B.8A-3C + 3H — five simplified popups. Only one is
            open at a time. When `editRecord` is set the shell mounts in
            edit mode (title = "Edit ...", PUT instead of POST on save).
            The Product Creation Studio is no longer reachable from this
            page — these popups are the sole create + edit surface. */}
        <CreateGlassModal
          open={openKind === SIMPLE_KINDS.GLASS}
          onClose={handleCloseModal}
          onSaved={handleSimpleSaved}
          editRecord={openKind === SIMPLE_KINDS.GLASS ? editRecord : null}
          initialValues={openKind === SIMPLE_KINDS.GLASS ? editInitialValues : null}
          viewOnly={openKind === SIMPLE_KINDS.GLASS && viewOnly}
        />
        <CreateHardwareModal
          open={openKind === SIMPLE_KINDS.HARDWARE}
          onClose={handleCloseModal}
          onSaved={handleSimpleSaved}
          editRecord={openKind === SIMPLE_KINDS.HARDWARE ? editRecord : null}
          initialValues={openKind === SIMPLE_KINDS.HARDWARE ? editInitialValues : null}
          viewOnly={openKind === SIMPLE_KINDS.HARDWARE && viewOnly}
        />
        <CreateProfileModal
          open={openKind === SIMPLE_KINDS.PROFILE}
          onClose={handleCloseModal}
          onSaved={handleSimpleSaved}
          editRecord={openKind === SIMPLE_KINDS.PROFILE ? editRecord : null}
          initialValues={openKind === SIMPLE_KINDS.PROFILE ? editInitialValues : null}
          viewOnly={openKind === SIMPLE_KINDS.PROFILE && viewOnly}
        />
        <CreateJobworkModal
          open={openKind === SIMPLE_KINDS.JOBWORK}
          onClose={handleCloseModal}
          onSaved={handleSimpleSaved}
          editRecord={openKind === SIMPLE_KINDS.JOBWORK ? editRecord : null}
          initialValues={openKind === SIMPLE_KINDS.JOBWORK ? editInitialValues : null}
          viewOnly={openKind === SIMPLE_KINDS.JOBWORK && viewOnly}
        />
        <CreateEcommerceModal
          open={openKind === SIMPLE_KINDS.ECOMMERCE}
          onClose={handleCloseModal}
          onSaved={handleSimpleSaved}
          editRecord={openKind === SIMPLE_KINDS.ECOMMERCE ? editRecord : null}
          initialValues={openKind === SIMPLE_KINDS.ECOMMERCE ? editInitialValues : null}
          viewOnly={openKind === SIMPLE_KINDS.ECOMMERCE && viewOnly}
        />

        {/* Stage 6B.8A-3H — read-only Set components viewer */}
        <ItemSetComponentsDrawer
          open={setDrawerOpen}
          recordId={setDrawerRecord && setDrawerRecord.id}
          recordLabel={setDrawerRecord
            ? (setDrawerRecord.itemName || setDrawerRecord.itemCode || 'Item Set')
            : null}
          onClose={closeSetDrawer}
          onEdit={canUpdate ? (id) => { closeSetDrawer(); goEdit(id); } : null}
        />
      </div>
    </ErpMasterShell>
  );
}
